<template>
  <b-modal
    visible
    title="Company Discount List"
    size="xl"
    scrollable
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
  >
    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$emit('modal-closed')"
      >
        Close
      </b-button>
    </template>

    <!-- Filters -->
    <b-row class="mb-2">
      <b-col md="4">
        <b-form-group label="Company">
          <custom-v-select
            v-model="selectedCompanies"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="companyOptions"
            :loading="companyOptionsLoading"
            label="text"
            placeholder="Select Company"
            :close-on-select="false"
            :deselect-from-dropdown="true"
            @input="handleCompanySelectChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Company Name">
          <b-form-input
            v-model="companyName"
            type="text"
            placeholder="Search Company Name"
            @input="debouncedFetch"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Discount Range">
          <b-row>
            <b-col cols="6">
              <b-form-input
                v-model.number="discountMin"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="Min %"
                @input="debouncedFetch"
              />
            </b-col>
            <b-col cols="6">
              <b-form-input
                v-model.number="discountMax"
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="Max %"
                @input="debouncedFetch"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
      <b-col
        md="2"
        class="d-flex align-items-end mb-1"
      >
        <b-button
          variant="primary"
          :disabled="selectedRows.length === 0"
          @click="openBulkUpdateModal"
        >
          Bulk Update ({{ selectedRows.length }})
        </b-button>
      </b-col>
    </b-row>

    <!-- Loading -->
    <div
      v-if="loading"
      class="text-center my-3"
    >
      <b-spinner
        label="Loading"
        variant="primary"
      />
    </div>

    <!-- Error -->
    <b-alert
      variant="danger"
      :show="!!loadingError"
    >
      <div class="alert-body">
        {{ loadingError }}
      </div>
    </b-alert>

    <!-- Table -->
    <div
      v-if="!loading && !loadingError"
      class="table-scroll-wrapper"
    >
      <b-table
        :items="results"
        :fields="tableFields"
        responsive="sm"
        striped
        hover
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        no-local-sorting
        class="compact-table"
        @sort-changed="sortingChanged"
      >
        <template #head(select)>
          <b-form-checkbox
            :checked="isAllSelected"
            :indeterminate="isIndeterminate"
            @change="toggleSelectAll"
          />
        </template>

        <template #cell(select)="{ item }">
          <b-form-checkbox
            :checked="isRowSelected(item.id)"
            @change="toggleRowSelection(item)"
          />
        </template>

        <template #cell(discount)="{ item }">
          <b-form-input
            v-if="editingId === item.id"
            :ref="`discountInput_${item.id}`"
            v-model.number="editDiscountValue"
            type="number"
            min="0"
            max="100"
            step="0.5"
            size="sm"
            style="width: 90px"
            placeholder="Enter %"
            @keyup.enter="saveDiscount(item)"
            @keyup.esc="cancelEdit"
          />
          <span v-else>
            {{ item.discount != null ? `${item.discount}%` : '-' }}
          </span>
        </template>

        <template #cell(action)="{ item }">
          <div
            v-if="editingId === item.id"
            class="d-flex align-items-center"
          >
            <feather-icon
              v-b-tooltip.hover="{boundary:'window'}"
              title="Save"
              icon="CheckIcon"
              size="18"
              class="cursor-pointer text-success"
              :class="{ 'pointer-events-none opacity-50': saving }"
              @click="saveDiscount(item)"
            />
            <feather-icon
              v-b-tooltip.hover="{boundary:'window'}"
              title="Cancel"
              icon="XIcon"
              size="18"
              class="cursor-pointer text-danger ml-half"
              @click="cancelEdit"
            />
          </div>
          <feather-icon
            v-else
            v-b-tooltip.hover="{boundary:'window'}"
            title="Edit Discount"
            icon="Edit2Icon"
            size="20"
            class="cursor-pointer"
            @click="startEdit(item)"
          />
        </template>

      </b-table>
    </div>

    <!-- Pagination -->
    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => { currentPage = page }"
    />

    <!-- Bulk Update Modal -->
    <b-modal
      v-model="showBulkModal"
      title="Bulk Update Discount"
      size="sm"
      centered
      no-close-on-backdrop
      @hidden="closeBulkModal"
    >
      <p class="mb-1">
        Updating discount for <strong>{{ selectedRows.length }}</strong> {{ selectedRows.length === 1 ? 'company' : 'companies' }}:
      </p>
      <ul
        class="small mb-2 pl-1"
        style="max-height: 150px; overflow-y: auto;"
      >
        <li
          v-for="row in selectedRows"
          :key="row.id"
        >
          {{ row.name }}
        </li>
      </ul>
      <b-form-group label="Discount (%)">
        <b-form-input
          ref="bulkDiscountInput"
          v-model.number="bulkDiscountValue"
          type="number"
          min="0"
          max="100"
          step="0.5"
          placeholder="Enter discount %"
          @keyup.enter="saveBulkDiscount"
        />
      </b-form-group>
      <template #modal-footer>
        <b-button
          variant="secondary"
          :disabled="bulkSaving"
          @click="closeBulkModal"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="bulkSaving || bulkDiscountValue === null || bulkDiscountValue === ''"
          @click="saveBulkDiscount"
        >
          <b-spinner
            v-if="bulkSaving"
            small
            class="mr-half"
          />
          Update All
        </b-button>
      </template>
    </b-modal>
  </b-modal>
</template>

<script>
import {
  BModal, BTable, BRow, BCol, BButton, BFormGroup, BFormInput,
  BSpinner, BAlert, VBTooltip, BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BModal,
    BTable,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BSpinner,
    BAlert,
    BFormCheckbox,
    CustomVSelect,
    DetailedPagination,
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      results: [],
      totalItems: 0,
      currentPage: 1,
      perPage: 50,
      sortBy: 'name',
      sortDesc: false,

      // Filters
      selectedCompanies: [],
      companyOptions: [],
      companyOptionsLoading: false,
      companyName: '',
      discountMin: null,
      discountMax: null,

      // Inline edit
      editingId: null,
      editDiscountValue: null,
      saving: false,

      // Bulk selection
      selectedRows: [],

      // Bulk update modal
      showBulkModal: false,
      bulkDiscountValue: null,
      bulkSaving: false,

      debounceTimer: null,

      tableFields: [
        { key: 'select', label: '', thStyle: { width: '40px' } },
        { key: 'name', label: 'Company Name', sortable: true },
        { key: 'discount', label: 'Discount (%)', sortable: true },
        { key: 'action', label: 'Action' },
      ],
    }
  },
  computed: {
    isAllSelected() {
      return this.results.length > 0 && this.selectedRows.length === this.results.length
    },
    isIndeterminate() {
      return this.selectedRows.length > 0 && this.selectedRows.length < this.results.length
    },
  },
  watch: {
    currentPage() {
      this.fetchCompanies()
    },
  },
  created() {
    this.fetchCompanyOptions()
    this.fetchCompanies()
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  methods: {
    debouncedFetch() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchCompanies()
      }, 500)
    },

    filtersChangeHandler() {
      this.currentPage = 1
      this.fetchCompanies()
    },

    handleCompanySelectChange(selected) {
      this.selectedCompanies = selected || []
      if (selected && selected.text) {
        this.companyName = selected.text
      } else {
        this.companyName = ''
      }
      this.filtersChangeHandler()
    },

    async fetchCompanyOptions() {
      this.companyOptionsLoading = true
      try {
        const companyRes = await axios.get('/company_options')
        this.companyOptions = companyRes.data.data.map(item => ({ text: item.text, id: item.text }))
      } catch {
        this.companyOptions = []
      } finally {
        this.companyOptionsLoading = false
      }
    },

    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchCompanies()
    },

    fetchCompanies() {
      this.loading = true
      this.selectedRows = []
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        sort_by: this.sortDesc ? `-${this.sortBy}` : this.sortBy,
      }

      if (this.companyName && this.companyName.trim()) {
        params.name = this.companyName.trim()
      }

      if (this.discountMin !== null && this.discountMin !== '') {
        params.discount_min = this.discountMin
      }
      if (this.discountMax !== null && this.discountMax !== '') {
        params.discount_max = this.discountMax
      }

      axios.get('/companies/', { params })
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Something went wrong'
          this.loading = false
        })
    },

    // --- Selection methods ---
    isRowSelected(id) {
      return this.selectedRows.some(row => row.id === id)
    },

    toggleRowSelection(item) {
      const index = this.selectedRows.findIndex(row => row.id === item.id)
      if (index !== -1) {
        this.selectedRows.splice(index, 1)
      } else {
        this.selectedRows.push(item)
      }
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedRows = [...this.results]
      } else {
        this.selectedRows = []
      }
    },

    // --- Bulk update modal ---
    openBulkUpdateModal() {
      this.bulkDiscountValue = null
      this.showBulkModal = true
      this.$nextTick(() => {
        if (this.$refs.bulkDiscountInput) {
          this.$refs.bulkDiscountInput.focus()
        }
      })
    },

    closeBulkModal() {
      this.showBulkModal = false
      this.bulkDiscountValue = null
    },

    async saveBulkDiscount() {
      if (this.bulkSaving) return
      if (this.bulkDiscountValue === null || this.bulkDiscountValue === '') return

      this.bulkSaving = true

      const failed = []
      const succeeded = []

      await Promise.all(
        this.selectedRows.map(async row => {
          const params = new URLSearchParams()
          params.append('discount', this.bulkDiscountValue)

          try {
            await axios.patch(`/companies/${row.id}/discount/`, params, {
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            })
            succeeded.push(row)
            const index = this.results.findIndex(r => r.id === row.id)
            if (index !== -1) {
              this.$set(this.results[index], 'discount', this.bulkDiscountValue)
            }
          } catch {
            failed.push(row)
          }
        }),
      )

      if (succeeded.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Bulk Update',
            text: `Discount updated for ${succeeded.length} ${succeeded.length === 1 ? 'company' : 'companies'}`,
            variant: 'success',
          },
        })
      }

      if (failed.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Partial Failure',
            text: `Failed to update ${failed.length}: ${failed.map(r => r.name).join(', ')}`,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }

      this.bulkSaving = false
      this.closeBulkModal()
      this.selectedRows = []
    },

    // --- Inline edit ---
    startEdit(item) {
      this.editingId = item.id
      this.editDiscountValue = null
      this.$nextTick(() => {
        const inputRef = this.$refs[`discountInput_${item.id}`]
        if (inputRef) {
          inputRef.focus()
        }
      })
    },

    cancelEdit() {
      this.editingId = null
      this.editDiscountValue = null
    },

    async saveDiscount(item) {
      if (this.saving) return
      if (this.editDiscountValue === null || this.editDiscountValue === '') {
        this.cancelEdit()
        return
      }
      this.saving = true

      const params = new URLSearchParams()
      params.append('discount', this.editDiscountValue)

      try {
        await axios.patch(`/companies/${item.id}/discount/`, params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })

        const index = this.results.findIndex(r => r.id === item.id)
        if (index !== -1) {
          this.$set(this.results[index], 'discount', this.editDiscountValue)
        }
        this.editingId = null
        this.editDiscountValue = null

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Discount Updated',
            text: `Discount for ${item.name} updated successfully`,
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Update Failed',
            text: error?.response?.data?.error
              || error?.response?.data?.detail
              || 'Something went wrong',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ml-half {
  margin-left: 0.25rem;
}

.mr-half {
  margin-right: 0.25rem;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.pointer-events-none {
  pointer-events: none;
}

.opacity-50 {
  opacity: 0.5;
}
</style>
